import {
	AddProductCompanyPayload,
	CompaniesInvoiceResult,
	CompaniesProductResult,
	CompaniesResult,
	CompaniesUsersResult,
	CompanyAccountManagerResult,
	CompanyDetails,
	CompanyEmployeesResult,
	CompanyListResult,
	CountriesResult,
	CreateEditCompanyPayload,
} from "@/modules/companies";
import { axiosInstance } from "../common";
import { toFormData } from "@/shared/utils";

const baseUrlAggregator = "aggregator/v1/delivery/companies";
const baseUrlAdministrator = "administration/companies";

export const getCompanies = async (filter: Record<string, string | number | boolean>): Promise<CompaniesResult> => {
	const page = filter?.page ? filter.page : 1;
	const status = filter.status ? filter.status : "";
	const limit = 10;
	const filterCompanyName = filter.companyName ? `&keywords=${filter.companyName}` : "";
	const { data } = await axiosInstance().get(
		`${baseUrlAggregator}?page=${page}&limit=${limit}${status ? `&status=${status}` : ""}${filterCompanyName}`
	);
	return data;
};

export const getCompanyById = async (id: string): Promise<CompanyDetails> => {
	const { data } = await axiosInstance().get(`${baseUrlAggregator}/${id}`);
	return data;
};

export const getCompanyInvoices = async (
	companyId: string,
	filter: Record<string, string | number | boolean>
): Promise<CompaniesInvoiceResult> => {
	const page = filter?.page ? filter.page : 1;
	const limit = 10;
	const { data } = await axiosInstance().get(`${baseUrlAggregator}/${companyId}/invoices?page=${page}&limit=${limit}`);
	return data;
};

export const getCompanyProducts = async (
	companyId: string,
	filter: Record<string, string | number | boolean>,
	locale: string
): Promise<CompaniesProductResult> => {
	const page = filter?.page ? filter.page : 1;
	const limit = 10;
	const { data } = await axiosInstance().get(
		`${baseUrlAggregator}/${companyId}/products?page=${page}&limit=${limit}&locale=${locale}`
	);
	return data;
};

export const getCompanyUsers = async (
	companyId: string,
	filter: Record<string, string | number | boolean>
): Promise<CompaniesUsersResult> => {
	const page = filter?.page ? filter.page : 1;
	const limit = 10;
	const { data } = await axiosInstance().get(`${baseUrlAggregator}/${companyId}/users?page=${page}&limit=${limit}`);
	return data;
};

export const getCompanyList = async (type?: string): Promise<CompanyListResult> => {
	const typeFilter = type?.length ? `?type=${type} ` : "";

	const { data } = await axiosInstance().get(`${baseUrlAggregator}/list${typeFilter}`);
	console.log("getCompanyList", data);

	return data;
};

export const getCompaniesAccountManagers = async (): Promise<CompanyAccountManagerResult> => {
	const { data } = await axiosInstance().get(`${baseUrlAggregator}/account-managers`);
	return data;
};

export const createCompany = async (payload: CreateEditCompanyPayload): Promise<void> => {
	const formData = toFormData<CreateEditCompanyPayload>(payload);

	await axiosInstance().post(baseUrlAdministrator, formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
};

export const editCompany = async (payload: CreateEditCompanyPayload): Promise<void> => {
	const { id, ...rest } = payload;

	const formData = toFormData<CreateEditCompanyPayload>(rest);

	await axiosInstance().patch(`${baseUrlAdministrator}/${id}`, formData, {
		headers: { "Content-Type": "multipart/form-data" },
	});
};

export const addProductToCompany = async (payload: AddProductCompanyPayload): Promise<void> => {
	await axiosInstance().patch(`${baseUrlAdministrator}/add-products`, payload);
};

export const updateCompanyStatus = async (companyId: number, status: string): Promise<void> => {
	await axiosInstance().patch(`${baseUrlAdministrator}/status`, { status, companyId });
};

export const getEmployeesCompany = async (
	companyId: string,
	filter: Record<string, string | boolean | number>
): Promise<CompanyEmployeesResult> => {
	const page = filter?.page ? filter.page : 1;
	const limit = 10;

	const { data } = await axiosInstance().get(
		`${baseUrlAggregator}/${companyId}/employees?page=${page}&limit=${limit}&locale=${filter.locale}`
	);
	return data;
};

export const updateLinkStatus = async (companyId: number, isEnabled: boolean) => {
	await axiosInstance().patch(`${baseUrlAdministrator}/link-status`, { companyId, isEnabled });
};

export const exportEmployees = async (
	companyId: string,
	locale: string,
	format: "csv" | "xlsx",
	signal: AbortSignal
): Promise<Blob> => {
	const { data } = await axiosInstance().get(
		`${baseUrlAggregator}/${companyId}/employees/export?format=${format}&locale=${locale}`,
		{
			responseType: "blob",
			signal,
		}
	);
	return data;
};

export const getCountries = async (): Promise<CountriesResult> => {
	const { data } = await axiosInstance().get(`${baseUrlAggregator}/countries`);
	return data;
};

export const getOpenDhcCompanies = async (
	companyId: string,
	filter: Record<string, string | number | boolean>
): Promise<CompanyEmployeesResult> => {
	const page = filter?.page ? filter.page : 1;
	const limit = 10;
	const { data } = await axiosInstance().get(
		`${baseUrlAggregator}/${companyId}/employees/open-dhc?page=${page}&limit=${limit}&locale=${filter.locale}`
	);
	return data;
};
